import * as Sentry from "@sentry/react";
import { getStoredConsent } from "@/components/CookieConsent";

type ConsentState = { analytics: boolean; marketing: boolean };
type QueuedEvent = { name: string; props?: Record<string, unknown> };

let initialized = false;
let analyticsEnabled = false;
let marketingEnabled = false;
const queue: QueuedEvent[] = [];

function readConsent(): ConsentState {
  const stored = getStoredConsent();
  return {
    analytics: Boolean(stored?.categories.analytics),
    marketing: Boolean(stored?.categories.marketing),
  };
}

function enableAnalyticsProviders() {
  if (!import.meta.env.PROD) return;
  // Sentry performance + session replay are gated on analytics consent.
  try {
    const client = Sentry.getClient();
    if (!client) return;
    const replay = client.getIntegrationByName?.("Replay") as unknown as
      | { start?: () => void }
      | undefined;
    replay?.start?.();
  } catch {
    // no-op
  }
}

function disableAnalyticsProviders() {
  try {
    const client = Sentry.getClient();
    if (!client) return;
    const replay = client.getIntegrationByName?.("Replay") as unknown as
      | { stop?: () => Promise<void> | void }
      | undefined;
    void replay?.stop?.();
  } catch {
    // no-op
  }
}

function flushQueue() {
  if (!analyticsEnabled) return;
  while (queue.length) {
    const evt = queue.shift()!;
    dispatchEvent(evt);
  }
}

function dispatchEvent(evt: QueuedEvent) {
  // Forward to Sentry breadcrumbs for now; swap in PostHog/Plausible later.
  try {
    Sentry.addBreadcrumb({
      category: "analytics",
      level: "info",
      message: evt.name,
      data: evt.props,
    });
  } catch {
    // no-op
  }
  if (!import.meta.env.PROD) {
    // eslint-disable-next-line no-console
    console.debug("[analytics]", evt.name, evt.props ?? {});
  }
}

function applyConsent(state: ConsentState) {
  const prevAnalytics = analyticsEnabled;
  analyticsEnabled = state.analytics;
  marketingEnabled = state.marketing;
  if (analyticsEnabled && !prevAnalytics) {
    enableAnalyticsProviders();
    flushQueue();
  } else if (!analyticsEnabled && prevAnalytics) {
    disableAnalyticsProviders();
  }
}

export function initAnalytics() {
  if (initialized) return;
  initialized = true;
  applyConsent(readConsent());
  window.addEventListener("emrkt:cookie-consent", (e) => {
    const detail = (e as CustomEvent).detail?.categories as
      | { analytics: boolean; marketing: boolean }
      | undefined;
    if (detail) applyConsent({ analytics: detail.analytics, marketing: detail.marketing });
  });
}

export function trackEvent(name: string, props?: Record<string, unknown>) {
  if (!analyticsEnabled) {
    // Drop silently when the user has not consented; keep a tiny queue only
    // for events fired immediately on load (max 20).
    if (queue.length < 20) queue.push({ name, props });
    return;
  }
  dispatchEvent({ name, props });
}

export function trackPageView(path: string) {
  trackEvent("page_view", { path });
}

export function hasAnalyticsConsent() {
  return analyticsEnabled;
}

export function hasMarketingConsent() {
  return marketingEnabled;
}